import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MESSAGES, RTL_LOCALES, type Locale, type MessageKey } from './messages.ts';

/**
 * Language, direction and number formatting.
 *
 * Direction is set on `<html>` rather than on a wrapper, so the whole document
 * — including scrollbars, form controls and the browser's own focus ring
 * behaviour — flips. Everything below uses CSS logical properties, so there is
 * no separate right-to-left stylesheet to keep in step.
 */

const STORAGE_KEY = 'pt.locale';

export interface I18n {
  readonly locale: Locale;
  readonly dir: 'ltr' | 'rtl';
  setLocale(locale: Locale): void;
  /** Translate, substituting `{name}` placeholders. */
  t(key: MessageKey, values?: Readonly<Record<string, string | number>>): string;
  /** Pick the right side of a `{ en, fa }` pair that came from the API. */
  pick(text: { readonly en: string; readonly fa: string } | undefined): string;
  formatNumber(value: number): string;
  formatDateTime(value: string | Date | null | undefined): string;
}

const I18nContext = createContext<I18n | undefined>(undefined);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'fa') return stored;
  } catch {
    // Private browsing can throw; the browser's own language is a fine default.
  }
  return navigator.language?.startsWith('fa') ? 'fa' : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }): ReactNode {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  useEffect(() => {
    const dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // A preference we cannot persist is still honoured for this visit.
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const value = useMemo<I18n>(() => {
    const table = MESSAGES[locale];
    // Persian digits for Persian, so numbers do not read as a foreign script
    // in the middle of a sentence.
    const numberFormat = new Intl.NumberFormat(locale === 'fa' ? 'fa-IR' : 'en-GB');
    const dateFormat = new Intl.DateTimeFormat(locale === 'fa' ? 'fa-IR' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

    return {
      locale,
      dir: RTL_LOCALES.has(locale) ? 'rtl' : 'ltr',
      setLocale,
      t(key, values) {
        const template = table[key] ?? key;
        if (!values) return template;
        return template.replace(/\{(\w+)\}/g, (match, name: string) => {
          const replacement = values[name];
          if (replacement === undefined) return match;
          return typeof replacement === 'number'
            ? numberFormat.format(replacement)
            : String(replacement);
        });
      },
      pick(text) {
        if (!text) return '';
        // Fall back to English rather than showing nothing: a missing
        // translation should degrade, not blank the screen.
        return (locale === 'fa' ? text.fa : text.en) || text.en || text.fa;
      },
      formatNumber: (value) => numberFormat.format(value),
      formatDateTime(value) {
        if (!value) return '';
        const date = typeof value === 'string' ? new Date(value) : value;
        return Number.isNaN(date.getTime()) ? '' : dateFormat.format(date);
      },
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside an I18nProvider');
  return value;
}

export type { Locale, MessageKey };
export { LOCALES } from './messages.ts';
